import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaDownload, FaEye, FaFileAlt, FaCalendar, FaUsers } from 'react-icons/fa';
import axios from 'axios';
import api from '../api/axiosInstance';
import NavigationBar from '../layout/NavigationBar';
import Footer from '../layout/footer';
import { toast } from 'react-toastify';

// Same join as EbookFileUpload.js (utils/apiUrl): works with or without a
// trailing slash on REACT_APP_APIURL, and fails clearly if it isn't set
import { apiEndpoint } from '../utils/apiUrl';

const EbooksManagement = () => {
  const navigate = useNavigate();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState('');

  useEffect(() => {
    const business_id = localStorage.getItem('business_id');
    if (business_id) {
      setBusinessId(business_id);
      fetchEbooks(business_id);
    } else {
      toast.error('Business ID not found');
      navigate('/login');
    }
  }, []);

  const fetchEbooks = async (businessId) => {
    try {
      setLoading(true);
      console.log('=== E-BOOKS DEBUG ===');
      console.log('Fetching products for business_id:', businessId);
      
      // Try fetching without business_id filter first to see all products
      const response = await api.get(`/gift/list`);
      
      console.log('API Response status:', response.data.status);
      console.log('Total products in response:', response.data.result?.length || 0);
      
      if (response.data.status === 'success') {
        // First, filter by business_id
        const businessProducts = response.data.result.filter(product => {
          // Handle both string and ObjectId formats
          const productBusinessId = product.business_id?._id || product.business_id;
          const matches = productBusinessId === businessId || 
                         productBusinessId?.toString() === businessId;
          
          if (matches) {
            console.log('Product belongs to business:', product.name);
          }
          return matches;
        });
        
        console.log('Products for this business:', businessProducts.length);
        
        // Log the first product to see its structure
        if (businessProducts.length > 0) {
          console.log('Sample product structure:', businessProducts[0]);
          console.log('Sample product keys:', Object.keys(businessProducts[0]));
        }
        
        // Then filter for digital products
        const digitalProducts = businessProducts.filter(product => {
          console.log(`Checking product "${product.name}":`, {
            is_digital: product.is_digital,
            product_type: product.product_type,
            digital_type: product.digital_type,
            file_key: product.file_key
          });
          
          const isDigital = product.is_digital === true || 
                           product.is_digital === 'true' ||
                           product.product_type === 'digital' ||
                           product.digital_type === 'ebook' ||
                           (product.file_key && product.file_key.includes('digital-products'));
          
          if (isDigital) {
            console.log('✅ Found digital product:', {
              id: product._id,
              name: product.name,
              is_digital: product.is_digital,
              product_type: product.product_type,
              digital_type: product.digital_type,
              file_key: product.file_key,
              business_id: product.business_id
            });
          }
          
          return isDigital;
        });
        
        console.log('Digital products found:', digitalProducts.length);
        console.log('Digital products array:', digitalProducts);
        console.log('===================');
        
        setEbooks(digitalProducts);
        
        if (digitalProducts.length === 0) {
          console.warn('No digital products found. Check if products have is_digital=true or product_type=digital');
        } else {
          console.log('✅ Successfully loaded', digitalProducts.length, 'e-books');
        }
      } else {
        console.error('API returned error status:', response.data);
        toast.error('Failed to load e-books');
      }
    } catch (error) {
      console.error('Error fetching e-books:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Error loading e-books');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadEbook = async (ebook) => {
    try {
      // Generate download URL for the business admin
      const response = await axios.get(
        apiEndpoint(`digital/admin-download/${ebook._id}`),
        {
          params: { business_id: businessId }
        }
      );

      if (response.data.status === 'success') {
        // Open download URL in new tab
        window.open(response.data.result.download_url, '_blank');
        toast.success('Download started');
      } else {
        toast.error(response.data.message || 'Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.message || 'Failed to download e-book');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">
                  <FaBook className="mr-2" />
                  E-Books Management
                </h1>
              </div>
              <div className="col-sm-6">
                <button
                  className="btn btn-primary float-right"
                  onClick={() => navigate('/add_gift')}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add New E-Book
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading e-books...</p>
              </div>
            ) : ebooks.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <FaBook style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
                  <h4>No E-Books Found</h4>
                  <p className="text-muted">You haven't added any e-books yet.</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/add_gift')}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Add Your First E-Book
                  </button>
                </div>
              </div>
            ) : (
              <div className="row">
                {ebooks.map((ebook) => (
                  <div key={ebook._id} className="col-md-6 col-lg-4 mb-4">
                    <div className="card h-100 shadow-sm">
                      {/* Cover Image */}
                      <div style={{ 
                        height: '250px', 
                        overflow: 'hidden',
                        backgroundColor: '#FDF9F2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {ebook.images && ebook.images.length > 0 ? (
                          <img
                            src={ebook.images[0]}
                            alt={ebook.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <FaBook style={{ fontSize: '80px', color: '#dee2e6' }} />
                        )}
                      </div>

                      <div className="card-body">
                        {/* Title */}
                        <h5 className="card-title" style={{ 
                          fontWeight: '600',
                          marginBottom: '10px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {ebook.name}
                        </h5>

                        {/* Description */}
                        <p className="card-text text-muted" style={{
                          fontSize: '14px',
                          height: '40px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {ebook.description || 'No description available'}
                        </p>

                        {/* File Info */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge badge-primary">
                              {ebook.file_format?.toUpperCase() || 'PDF'}
                            </span>
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              {formatFileSize(ebook.file_size)}
                            </span>
                          </div>
                          
                          <div className="d-flex justify-content-between text-muted" style={{ fontSize: '12px' }}>
                            <span>
                              <FaCalendar className="mr-1" />
                              {formatDate(ebook.created_date)}
                            </span>
                            {/* <span>
                              <FaUsers className="mr-1" />
                              {ebook.sold_quantity || 0} sold
                            </span> */}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="mb-3">
                          <h4 className="text-success mb-0">
                            ₹{parseFloat(ebook.price).toFixed(2)}
                          </h4>
                        </div>

                        {/* Download Limit */}
                        <div className="mb-3">
                          <small className="text-muted">
                            Download Limit: {ebook.download_limit || 5} times
                          </small>
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm flex-fill"
                            onClick={() => handleDownloadEbook(ebook)}
                          >
                            <FaDownload className="mr-1" />
                            Download
                          </button>
                          {/* <button
                            className="btn btn-primary btn-sm flex-fill"
                            onClick={() => navigate(`/add_gift?id=${ebook._id}`)}
                          >
                            <FaEye className="mr-1" />
                            View/Edit
                          </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EbooksManagement;
